/**
 * 工作中心（/app/）島的資料層——逐行複刻 public/app/work_center.js 的聚合邏輯，
 * 只把 fetchWithAuth → 島共用 api()、innerHTML → React。
 */
import { api, type BoundApi } from "../../lib/api";
import type { HomeUser, TodoGroups, TodoItem } from "./types";

type RawBatch = {
	id?: number | string;
	amounts?: { settlementNet?: number; net?: number };
	settlementNet?: number;
	viewerRole?: string;
	ownerName?: string;
	ownerUsername?: string;
	agentName?: string;
	agentUsername?: string;
	counterpartyName?: string;
	counterpartyUsername?: string;
	ledgerName?: string;
	periodLabel?: string;
	period?: string;
	status?: string;
	[key: string]: unknown;
};

type RawDelivery = {
	id?: number | string;
	settlement_net?: number;
	perspective_net?: number;
	net_amount?: number;
	amount?: number;
	sender_name?: string;
	sender_username?: string;
	recipient_name?: string;
	recipient_username?: string;
	statement?: { period_start?: string; period_end?: string };
	period_label?: string;
	status?: string;
	sender_user_id?: number;
	recipient_user_id?: number;
	[key: string]: unknown;
};

type RawLedger = {
	id?: number | string;
	name?: string;
	ledger_date?: string;
	status?: string;
	is_owner?: boolean;
	unsettled_count?: number;
	owner_settlement_net?: number;
	has_batches?: boolean;
	[key: string]: unknown;
};

type BatchResp = { batches?: RawBatch[]; items?: RawBatch[] };
type DelResp = { deliveries?: RawDelivery[] };
type LedgerResp = { ledgers?: RawLedger[] };

async function safe<T>(p: Promise<T>, fallback: T): Promise<T> {
	try {
		return await p;
	} catch {
		return fallback;
	}
}

function normalizeBatch(b: RawBatch, source: "paper" | "platform"): TodoItem {
	const amounts = b.amounts || {};
	const net = amounts.settlementNet ?? amounts.net ?? b.settlementNet ?? null;
	const counterparty =
		b.viewerRole === "agent"
			? b.ownerName || b.ownerUsername
			: b.agentName || b.agentUsername || b.counterpartyName || b.counterpartyUsername || "";
	const period = b.ledgerName || b.periodLabel || b.period || "";
	const status = b.status || "";
	return {
		id: b.id ?? "",
		source,
		status,
		title: counterparty || (source === "paper" ? `紙單分帳 #${b.id}` : `平台分帳 #${b.id}`),
		subtitle: [period, status === "issued" ? "待確認過帳" : status].filter(Boolean).join(" · "),
		amount: net,
		href: `/app/settlement/inbox.html?source=${source}&status=${encodeURIComponent(status)}#batch-${b.id}`,
		statusLabel: status === "issued" ? "待確認" : status === "disputed" ? "已駁回" : status || "分帳",
		statusTone: status === "disputed" ? "rose" : "amber",
		cta: status === "issued" ? "去確認" : "查看",
	};
}

function normalizeDelivery(d: RawDelivery, bucket: "confirm" | "waiting"): TodoItem {
	const amount = d.settlement_net ?? d.perspective_net ?? d.net_amount ?? d.amount ?? null;
	const other =
		bucket === "confirm" ? d.sender_name || d.sender_username || "上線" : d.recipient_name || d.recipient_username || "下線";
	const period = d.statement
		? [d.statement.period_start, d.statement.period_end].filter(Boolean).join(" ~ ")
		: d.period_label || "";
	const status = d.status || "";
	return {
		id: d.id ?? "",
		source: "delivery",
		status,
		title: other,
		subtitle: [period, `#${d.id}`].filter(Boolean).join(" · "),
		amount,
		href: `/app/settlement/delivery.html?id=${d.id}`,
		statusLabel:
			status === "issued"
				? bucket === "confirm"
					? "待我確認"
					: "待對方確認"
				: status === "disputed"
					? "已駁回"
					: status === "void_pending"
						? "待作廢核可"
						: status || "平台交收",
		statusTone: status === "disputed" || status === "void_pending" ? "rose" : "sky",
		cta: bucket === "confirm" ? "去確認" : "查看",
	};
}

/** 待辦佇列：邏輯同 public/app/work_center.js 的 loadTodoGroups。 */
export async function loadTodoGroups(api: BoundApi, user: HomeUser, opts: { isAdmin: boolean }): Promise<TodoGroups> {
	const empty: TodoGroups = { confirm: [], today: [], stuck: [] };
	if (opts.isAdmin) return empty;

	const [delConfirm, delAll, paperIssued, paperDisputed, platformIssued, platformDisputed, paperLedgers] = await Promise.all([
		safe(api<DelResp>("/api/delivery/deliveries?role=recipient&status=issued"), {}),
		safe(api<DelResp>("/api/delivery/deliveries"), {}),
		safe(api<BatchResp>("/api/paper-settlement/batches?status=issued"), {}),
		safe(api<BatchResp>("/api/paper-settlement/batches?status=disputed"), {}),
		safe(api<BatchResp>("/api/platform-settlement/batches?status=issued"), {}),
		safe(api<BatchResp>("/api/platform-settlement/batches?status=disputed"), {}),
		safe(api<LedgerResp>("/api/paper-settlement/ledgers"), {}),
	]);

	const confirm: TodoItem[] = [];
	for (const d of delConfirm.deliveries || []) confirm.push(normalizeDelivery(d, "confirm"));
	for (const b of paperIssued.batches || paperIssued.items || []) confirm.push(normalizeBatch(b, "paper"));
	for (const b of platformIssued.batches || platformIssued.items || []) confirm.push(normalizeBatch(b, "platform"));

	const today: TodoItem[] = [];
	for (const l of paperLedgers.ledgers || []) {
		if (!l.is_owner) continue;
		if (l.status === "open" || !l.status) {
			today.push({
				id: `ledger-open-${l.id}`,
				source: "paper",
				title: l.name || `帳表 #${l.id}`,
				subtitle: [l.ledger_date, l.unsettled_count ? `未結 ${l.unsettled_count}` : "開帳中"].filter(Boolean).join(" · "),
				amount: l.owner_settlement_net ?? null,
				href: "/app/paper/close.html",
				statusLabel: "待關帳",
				statusTone: "amber",
				cta: "去關帳",
				status: l.status || "",
			});
		} else if (l.status === "closed" && !l.has_batches) {
			today.push({
				id: `ledger-issue-${l.id}`,
				source: "paper",
				title: l.name || `帳表 #${l.id}`,
				subtitle: [l.ledger_date, "已關帳，尚未出具分帳"].filter(Boolean).join(" · "),
				amount: l.owner_settlement_net ?? null,
				href: `/app/paper/ledgers.html#ledger-${l.id}`,
				statusLabel: "待出具",
				statusTone: "sky",
				cta: "去出具",
				status: l.status || "",
			});
		}
	}

	const stuck: TodoItem[] = [];
	const uid = user?.id;
	for (const d of delAll.deliveries || []) {
		if (d.status === "issued" && d.sender_user_id === uid) {
			stuck.push(normalizeDelivery(d, "waiting"));
		} else if (d.status === "disputed" && (d.sender_user_id === uid || d.recipient_user_id === uid)) {
			stuck.push(normalizeDelivery(d, "waiting"));
		} else if (d.status === "void_pending" && (d.sender_user_id === uid || d.recipient_user_id === uid)) {
			stuck.push(normalizeDelivery(d, "waiting"));
		}
	}
	for (const b of paperDisputed.batches || paperDisputed.items || []) stuck.push(normalizeBatch(b, "paper"));
	for (const b of platformDisputed.batches || platformDisputed.items || []) stuck.push(normalizeBatch(b, "platform"));

	return { confirm, today, stuck };
}
